/**
 * Formattazione per l'interfaccia. Tutto in italiano e nel fuso `Europe/Rome`: i timestamp
 * arrivano dal server in ISO 8601 UTC e vanno mostrati come li leggerebbe chi è al poligono.
 */

import { useEffect, useState } from 'react';
import { FUSO, giornata } from './tempo';

const GIORNO_ESTESO = new Intl.DateTimeFormat('it-IT', {
  timeZone: FUSO,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/** «Sabato 1 agosto» — senza anno, come nei mockup. */
const GIORNO_ESTESO_SENZA_ANNO = new Intl.DateTimeFormat('it-IT', {
  timeZone: FUSO,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/** «Sab 25 luglio» — forma abbreviata dello storico. */
const GIORNO_ABBREVIATO = new Intl.DateTimeFormat('it-IT', {
  timeZone: FUSO,
  weekday: 'short',
  day: 'numeric',
  month: 'long',
});

const GIORNO_BREVE = new Intl.DateTimeFormat('it-IT', {
  timeZone: FUSO,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/* Giorno e mese senza anno: la colonna «Quando» del registro, dove l'anno è nel filtro. */
const GIORNO_MESE = new Intl.DateTimeFormat('it-IT', {
  timeZone: FUSO,
  day: '2-digit',
  month: '2-digit',
});

const ORA = new Intl.DateTimeFormat('it-IT', {
  timeZone: FUSO,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function maiuscolaIniziale(testo: string): string {
  return testo.charAt(0).toUpperCase() + testo.slice(1);
}

function comeIstante(quando: string | Date): Date {
  // Una data `YYYY-MM-DD` è una giornata, non un istante: si àncora a mezzogiorno per non
  // scivolare al giorno prima o dopo per effetto del fuso.
  return new Date(
    typeof quando === 'string' && quando.length === 10 ? `${quando}T12:00:00Z` : quando,
  );
}

/**
 * «Venerdì 31 luglio» — l'anno compare solo se diverso da quello corrente, come nei mockup:
 * chi legge «sabato 8 agosto» sa già di che anno si parla.
 */
export function dataEstesa(quando: string | Date = new Date()): string {
  const d = comeIstante(quando);
  /*
   * L'anno si confronta sulla giornata italiana, non su quella di Greenwich.
   *
   * Tutta la formattazione di questo modulo passa da `Europe/Rome`; questo confronto no. Nelle
   * prime ore del 1° gennaio — fino all'una in ora solare — l'anno secondo UTC è ancora
   * quello vecchio, e la data di oggi comparirebbe con l'anno scritto accanto mentre tutte le
   * altre non ce l'hanno.
   */
  const stessoAnno = giornata(d).slice(0, 4) === giornata().slice(0, 4);
  const formato = stessoAnno ? GIORNO_ESTESO_SENZA_ANNO : GIORNO_ESTESO;
  return maiuscolaIniziale(formato.format(d));
}

/** «Sab 25 luglio» — forma abbreviata usata nello storico (4f, 4m). */
export function dataAbbreviata(quando: string | Date): string {
  return maiuscolaIniziale(GIORNO_ABBREVIATO.format(comeIstante(quando))).replace('.', '');
}

/** «Venerdì 31 luglio 2026», con l'anno sempre presente. */
export function dataEstesaConAnno(quando: string | Date = new Date()): string {
  return maiuscolaIniziale(GIORNO_ESTESO.format(comeIstante(quando)));
}

/** «31/07/2026» — da ISO oppure da `YYYY-MM-DD`. */
export function dataBreve(quando: string | Date): string {
  return GIORNO_BREVE.format(comeIstante(quando));
}

/**
 * «07/09 20:14» — giorno, mese e ora, tutti e tre in `Europe/Rome`.
 *
 * Il registro se lo componeva a mano con `d.getDate()` e `d.getMonth()`, che leggono il fuso
 * del dispositivo, e ci concatenava `ora()`, che invece formatta su Roma: le due metà della
 * stessa cella potevano riferirsi a due giorni diversi. Con un solo formattatore non può
 * più succedere, e chi ne ha bisogno altrove lo trova già fatto.
 */
export function giornoEOra(quando: string | Date): string {
  return `${GIORNO_MESE.format(comeIstante(quando))} ${ora(quando)}`;
}

/** «08:12» */
export function ora(quando: string | Date = new Date()): string {
  return ORA.format(new Date(quando));
}

/** «Venerdì 31 luglio 2026 · 08:12» */
export function dataEOra(quando: string | Date = new Date()): string {
  return `${dataEstesa(quando)} · ${ora(quando)}`;
}

const ISTANTE = new Intl.DateTimeFormat('it-IT', {
  timeZone: FUSO,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/**
 * «31/07/2026 14:22:08 (Europe/Rome)» — la forma della schermata 6k.
 *
 * Il fuso è scritto per esteso perché questo dato serve a cercare una riga nei log, e chi
 * cerca lavora su registri in UTC: senza l'indicazione sbaglierebbe di un'ora o due.
 */
export function istanteConFuso(quando: string | Date = new Date()): string {
  return `${ISTANTE.format(new Date(quando)).replace(',', '')} (${FUSO})`;
}

/** Ore intere fra due istanti, arrotondate: «Sono passate 8 ore». */
export function oreFra(daIso: string, aIso: string): number {
  return Math.round(
    (new Date(aIso).getTime() - new Date(daIso).getTime()) / 3_600_000,
  );
}

// ────────────────────────────────────────────────────────────────────── formato pagina

export type Formato = 'mobile' | 'tablet' | 'desktop';

/**
 * Formato della composizione, scelto dal viewport. Non ha nulla a che vedere con la
 * classificazione del dispositivo fatta dal server (R-23): quella decide cosa si può fare,
 * questa soltanto come si dispone la pagina.
 */
function formatoDa(larghezza: number): Formato {
  if (larghezza < 768) return 'mobile';
  if (larghezza < 1280) return 'tablet';
  return 'desktop';
}

export function useFormato(): Formato {
  const [formato, setFormato] = useState<Formato>(() =>
    formatoDa(typeof window === 'undefined' ? 1440 : window.innerWidth),
  );

  useEffect(() => {
    const suRidimensiona = () => setFormato(formatoDa(window.innerWidth));
    window.addEventListener('resize', suRidimensiona);
    return () => window.removeEventListener('resize', suRidimensiona);
  }, []);

  return formato;
}

/** Densità da applicare a un formato: compatta solo su desktop. */
export function densitaDa(formato: Formato): 'tattile' | 'compatta' {
  return formato === 'desktop' ? 'compatta' : 'tattile';
}
